/**
 * Embed snippet generation for the export dialog.
 *
 * Pure string builders (no DOM) so the iframe snippet, embed URL, snapshot
 * filename, and size warning can be unit-tested and reused. The dialog in
 * `components/EmbedDialog.tsx` is the only production consumer.
 *
 * Usage:
 *   const url = buildEmbedUrl(window.location.origin, encoded);
 *   const html = buildIframeSnippet({ origin, encodedPayload, title });
 */

/**
 * Above this many encoded characters the dialog suggests the blog-hosted
 * snapshot file (`?src=`) instead of the URL fragment: some platforms
 * truncate very long URLs (~8k chars is a common limit).
 */
export const EMBED_PAYLOAD_WARN_CHARS = 8000;

/** Inputs for {@link buildIframeSnippet}. */
interface EmbedSnippetInput {
  /** Deployment origin, e.g. `https://vibediagram.example` (no trailing slash). */
  origin: string;
  /** The base64url payload produced by `encodeEmbedPayload`. */
  encodedPayload: string;
  /** Project title — becomes the iframe's accessible `title` attribute. */
  title: string;
}

/** Build the standalone embed URL carrying the snapshot in the fragment. */
export function buildEmbedUrl(origin: string, encodedPayload: string): string {
  // base64url uses only A-Z a-z 0-9 - _ so no escaping is needed.
  return `${origin}/embed#data=${encodedPayload}`;
}

/**
 * Build the copy-pastable iframe tag for a blog post: sandboxed (the embed
 * runs arbitrary user code), lazy-loaded, with explicit dimensions so the
 * host page does not shift layout. 800x600 fits the embed page's
 * header + diagram + footer column layout comfortably.
 */
export function buildIframeSnippet(input: EmbedSnippetInput): string {
  const src = buildEmbedUrl(input.origin, input.encodedPayload);
  const title = escapeHtmlAttribute(input.title);
  return (
    // allow-popups lets the footer attribution link (target="_blank") open;
    // it does not weaken script isolation — the frame keeps its opaque origin.
    `<iframe src="${src}" sandbox="allow-scripts allow-popups" loading="lazy" ` +
    `width="800" height="600" title="${title}"></iframe>`
  );
}

/** Whether the encoded payload is long enough to risk URL truncation. */
export function isPayloadOversized(encodedPayload: string): boolean {
  return encodedPayload.length > EMBED_PAYLOAD_WARN_CHARS;
}

/**
 * Derive a download filename for the snapshot JSON from the project title,
 * e.g. "Cache Layer" → "cache-layer.diagram.json". Falls back to "diagram"
 * when the title has no usable characters.
 */
export function snapshotFileName(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "diagram"}.diagram.json`;
}

/** Escape a string for use inside a double-quoted HTML attribute. */
function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
